import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Loader2, Key, Code, Copy, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ setToken }) {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiAccessCode, setApiAccessCode] = useState('');
  const [showApiInfo, setShowApiInfo] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, {
        username,
        password,
      });

      setToken(response.data.token);
      toast.success(isLogin ? 'Bienvenido!' : 'Cuenta creada exitosamente');
      navigate('/');
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error en la autenticación'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApiAccessCode = (e) => {
    e.preventDefault();
    if (apiAccessCode === '2508') {
      setShowApiInfo(true);
      toast.success('Acceso concedido a la API');
    } else {
      toast.error('Código de acceso incorrecto');
      setShowApiInfo(false);
    }
  };

  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text);
    toast.success(message);
  };

  const apiKey = 'c2ZFmIgazCaRA4Mz41SXdxhlJwjqCJDz4gUn33kiXyM';
  // Use backend URL from environment variable
  const backendUrl = BACKEND_URL || 'http://localhost:8001';
  const baseUrl = `${backendUrl}/api/public`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-4xl space-y-6">
        {/* Login Card */}
        <Card className="w-full max-w-md mx-auto shadow-2xl border-0" data-testid="login-card">
          <CardHeader className="space-y-4 text-center pb-8">
            <div className="mx-auto w-32 h-32 flex items-center justify-center" data-testid="logo-icon">
              <img src="/logo.png" alt="Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                Gestión de Expedientes
              </CardTitle>
              <CardDescription className="text-base mt-2">
                {isLogin ? 'Inicia sesión para continuar' : 'Crea tu cuenta de digitalizador'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium">
                  Usuario
                </Label>
                <Input
                  id="username"
                  data-testid="username-input"
                  type="text"
                  placeholder="Ingresa tu usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Contraseña
                </Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                data-testid="submit-button"
                className="w-full h-11 bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-medium shadow-md transition-all duration-200"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Procesando...
                  </>
                ) : isLogin ? (
                  'Iniciar Sesión'
                ) : (
                  'Registrarse'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors"
              >
                {isLogin
                  ? '¿No tienes cuenta? Regístrate'
                  : '¿Ya tienes cuenta? Inicia sesión'}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* API Pública Section */}
        <Card className="w-full max-w-md mx-auto shadow-xl border-0 bg-gradient-to-br from-purple-50 to-blue-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                <Key className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">API Pública</h3>
                <p className="text-sm text-gray-600">Acceso para desarrolladores</p>
              </div>
            </div>

            {!showApiInfo ? (
              <form onSubmit={handleApiAccessCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="apiAccessCode" className="text-sm font-medium text-gray-700">
                    Código de Acceso
                  </Label>
                  <Input
                    id="apiAccessCode"
                    type="password"
                    placeholder="Ingresa el código"
                    value={apiAccessCode}
                    onChange={(e) => setApiAccessCode(e.target.value)}
                    className="h-10"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-10 bg-purple-600 hover:bg-purple-700"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ver Información de API
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowApiInfo(false)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <EyeOff className="w-4 h-4 mr-1" />
                    Ocultar
                  </Button>
                </div>

                {/* API Key */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    API Key
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={apiKey}
                      readOnly
                      className="font-mono text-xs bg-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(apiKey, 'API Key copiada')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Base URL */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Base URL
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={baseUrl}
                      readOnly
                      className="font-mono text-xs bg-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(baseUrl, 'URL copiada')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Header Requerido */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Header Requerido
                  </Label>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <code className="text-xs text-gray-700">
                      X-API-Key: {apiKey}
                    </code>
                  </div>
                </div>

                {/* Endpoints */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    Endpoints Disponibles
                  </Label>
                  <div className="bg-white rounded-lg p-3 space-y-1 border border-gray-200 text-xs">
                    <div className="text-gray-700">• GET /expedientes</div>
                    <div className="text-gray-700">• GET /expedientes/{'{id}'}</div>
                    <div className="text-gray-700">• GET /expedientes/buscar/cedula/{'{cedula}'}</div>
                    <div className="text-gray-700">• GET /expedientes/buscar/nombre/{'{nombre}'}</div>
                    <div className="text-gray-700">• GET /api-info</div>
                  </div>
                </div>

                {/* Ejemplo */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Ejemplo cURL
                  </Label>
                  <div className="bg-gray-900 text-green-400 rounded-lg p-3 text-xs font-mono overflow-x-auto">
                    curl -H "X-API-Key: {apiKey}" "{baseUrl}/expedientes"
                  </div>
                </div>

                {/* Documentación */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  <strong>📚 Documentación Completa:</strong> Solicita el archivo API_DOCUMENTATION.md para más detalles
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}